import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Zap, Server, Coins, TrendingUp, Shield, Globe, 
  ExternalLink, ArrowRight, Wallet, BarChart3, 
  CreditCard, PieChart, Bot, Lock, Layers, Cpu
} from 'lucide-react';

// Project Pi Labs Services
const piLabsServices = [
  {
    id: 'liquid-staking',
    title: 'Liquid Staking',
    description: 'Earn 8-12% APY while maintaining liquidity of your assets',
    icon: Coins,
    apy: '8-12% APY',
    link: 'https://www.pipools.xyz/liquid-staking/',
    color: 'emerald',
    features: ['Maintain liquidity', 'Earn passive income', 'No lockup period']
  },
  {
    id: 'validation',
    title: 'Validator Services',
    description: 'Run validators and earn 43-107% APR on supported networks',
    icon: Server,
    apy: '43-107% APR',
    link: 'https://www.pipools.xyz/pipooloption/',
    color: 'blue',
    features: ['Multiple networks', 'High yields', 'Professional management']
  },
  {
    id: 'physical-nodes',
    title: 'Physical Nodes',
    description: 'Purchase and own your own validator hardware',
    icon: Cpu,
    price: 'From $499',
    link: 'https://commerce.coinbase.com/checkout/48cab06c-6803-49f6-b83f-070514ae155f',
    color: 'purple',
    features: ['Own your hardware', 'Full control', 'Dedicated support']
  },
  {
    id: 'consultation',
    title: 'Web3 Consultation',
    description: 'Expert guidance for founders, startups, and institutions',
    icon: Shield,
    link: 'https://calendly.com/projectpilabs/30min',
    color: 'orange',
    features: ['Strategy sessions', 'Technical guidance', 'Implementation support']
  }
];

// Liquid Agent AI Services
const liquidAgentServices = [
  {
    id: 'payments',
    title: 'Smart Payments',
    description: 'Schedule automated payments with your Web3 wallet',
    icon: CreditCard,
    color: 'cyan',
    features: ['Automated scheduling', 'Multi-chain support', 'Gas optimization']
  },
  {
    id: 'staking',
    title: 'AI-Powered Staking',
    description: 'Intelligent staking strategies optimized by AI',
    icon: TrendingUp,
    color: 'emerald',
    features: ['Auto-compounding', 'Risk management', 'Yield optimization']
  },
  {
    id: 'lending',
    title: 'DeFi Lending',
    description: 'Access decentralized lending protocols seamlessly',
    icon: Wallet,
    color: 'blue',
    features: ['Competitive rates', 'Collateral management', 'Flash loans']
  },
  {
    id: 'insights',
    title: 'Market Insights',
    description: 'AI-driven analytics and market intelligence',
    icon: BarChart3,
    color: 'purple',
    features: ['Real-time data', 'Trend analysis', 'Portfolio tracking']
  }
];

// Supported Networks
const supportedNetworks = [
  { name: 'Base', logo: 'https://www.projectpilabs.com/images/Base.png' },
  { name: 'SUI', logo: 'https://www.projectpilabs.com/images/SUI.png' },
  { name: 'PulseChain', logo: 'https://www.projectpilabs.com/images/PulseChain.png' },
  { name: 'XRPL', logo: 'https://www.projectpilabs.com/images/XRPL.png' },
  { name: 'XDC', logo: 'https://www.projectpilabs.com/images/XDC.png' },
  { name: 'Ethereum', logo: 'https://www.projectpilabs.com/images/Ethereum.png' },
];

export default function Web3Services() {
  const [activeTab, setActiveTab] = useState('pilabs');

  const colorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pt-16" data-testid="web3-services-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 py-16 relative">
          <div className="text-center mb-12">
            <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-4">
              <Zap className="h-3 w-3 mr-1" /> Web3 Infrastructure Partner
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Next-Generation <span className="text-[#D4AF37]">Web3 Services</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Powered by Project Pi Labs & Liquid Agent AI - Your gateway to yield-generating 
              infrastructure, liquid staking, and AI-powered DeFi solutions.
            </p>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto mb-12">
            <div className="text-center p-4 bg-[#14120f] rounded-lg border border-[#1c1814]">
              <p className="text-2xl font-bold text-[#D4AF37]">1,252+</p>
              <p className="text-gray-500 text-sm">Active Users</p>
            </div>
            <div className="text-center p-4 bg-[#14120f] rounded-lg border border-[#1c1814]">
              <p className="text-2xl font-bold text-emerald-400">107%</p>
              <p className="text-gray-500 text-sm">Max APR</p>
            </div>
            <div className="text-center p-4 bg-[#14120f] rounded-lg border border-[#1c1814]">
              <p className="text-2xl font-bold text-blue-400">$80K+</p>
              <p className="text-gray-500 text-sm">AUM</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8 bg-[#14120f]">
            <TabsTrigger 
              value="pilabs" 
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            >
              <Server className="h-4 w-4 mr-2" />
              Project Pi Labs
            </TabsTrigger>
            <TabsTrigger 
              value="liquidagent"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            >
              <Bot className="h-4 w-4 mr-2" />
              Liquid Agent AI
            </TabsTrigger>
          </TabsList>

          {/* Project Pi Labs Tab */}
          <TabsContent value="pilabs">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold mb-2">Yield Generating Infrastructure</h2>
              <p className="text-gray-400">
                Atlanta-based company building yield infrastructure through validators, liquid staking, and on-chain AI.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {piLabsServices.map((service) => (
                <Card 
                  key={service.id} 
                  className="bg-[#14120f] border-[#1c1814] hover:border-[#D4AF37]/50 transition-all group"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`w-12 h-12 rounded-lg ${colorClasses[service.color]} flex items-center justify-center`}>
                        <service.icon className="h-6 w-6" />
                      </div>
                      {service.apy && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                          {service.apy}
                        </Badge>
                      )}
                      {service.price && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                          {service.price}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl mt-4">{service.title}</CardTitle>
                    <CardDescription className="text-gray-400">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <a href={service.link} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#B8962E] group-hover:shadow-lg group-hover:shadow-[#D4AF37]/20">
                        Get Started <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Supported Networks */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-center mb-6">Supported Networks</h3>
              <div className="flex flex-wrap justify-center gap-4">
                {supportedNetworks.map((network) => (
                  <div 
                    key={network.name}
                    className="flex items-center gap-2 px-4 py-2 bg-[#14120f] border border-[#1c1814] rounded-lg hover:border-[#D4AF37]/30 transition-colors"
                  >
                    <img src={network.logo} alt={network.name} className="w-6 h-6" />
                    <span className="text-sm text-gray-300">{network.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <a href="https://projectpilabs.com" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                  Visit Project Pi Labs <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </TabsContent>

          {/* Liquid Agent AI Tab */}
          <TabsContent value="liquidagent">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold mb-2">The Financial Layer for Web3 Simplicity</h2>
              <p className="text-gray-400">
                AI-powered tools for payments, staking, lending, and market insights - all in one platform.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {liquidAgentServices.map((service) => (
                <Card 
                  key={service.id} 
                  className="bg-[#14120f] border-[#1c1814] hover:border-[#D4AF37]/50 transition-all group"
                >
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-lg ${colorClasses[service.color]} flex items-center justify-center`}>
                      <service.icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl mt-4">{service.title}</CardTitle>
                    <CardDescription className="text-gray-400">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                          <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Liquid Agent CTA */}
            <div className="bg-gradient-to-r from-cyan-500/10 via-[#14120f] to-purple-500/10 rounded-2xl p-8 text-center border border-[#1c1814]">
              <Bot className="h-12 w-12 text-cyan-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Ready to Experience Web3 Simplicity?</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Connect your wallet and let Liquid Agent AI handle the complexity while you focus on building wealth.
              </p>
              <a href="https://liquidagent.ai" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white hover:opacity-90">
                  Launch Liquid Agent <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </TabsContent>
        </Tabs>

        {/* Partnership Banner */}
        <div className="mt-16 p-8 bg-[#14120f] rounded-2xl border border-[#1c1814]">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-2">
                Strategic Partnership
              </Badge>
              <h3 className="text-xl font-bold">Nouveau Riche Legacy × Project Pi Labs</h3>
              <p className="text-gray-400 mt-1">
                Building generational wealth through traditional finance and Web3 infrastructure.
              </p>
            </div>
            <div className="flex gap-4">
              <a href="https://calendly.com/projectpilabs/30min" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#B8962E]">
                  Book a Consultation
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
